// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { z } from 'zod';

// A Click or Write inside Detect Transaction Details that could not be done.
// It travels from the RBS runtime to the payments sidebar as this code,
// followed by an operator-facing message naming the element, in the
// workflow error text, the way a value transform failure does.
export const RBS_IPS_ACTION_FAILED_ERROR_CODE = 'RBS_IPS_ACTION_FAILED' as const;

export interface RbsIpsActionFailureResult {
    readonly status: 'action_failed';
    readonly errorCode: typeof RBS_IPS_ACTION_FAILED_ERROR_CODE;
    readonly message: string;
}

export const RbsIpsActionFailureResultSchema: z.ZodType<RbsIpsActionFailureResult> = z
    .object({
        status: z.literal('action_failed'),
        errorCode: z.literal(RBS_IPS_ACTION_FAILED_ERROR_CODE),
        message: z.string().min(1),
    })
    .strict();

const RbsIpsActionFailureErrorPrefix = `${RBS_IPS_ACTION_FAILED_ERROR_CODE}: `;

export function formatRbsIpsActionFailureError(message: string): string {
    return RbsIpsActionFailureErrorPrefix + message;
}

// The message of an action failure, or undefined for any other error.
export function readRbsIpsActionFailureError(error: string | undefined): string | undefined {
    return error?.startsWith(RbsIpsActionFailureErrorPrefix) === true
        ? error.slice(RbsIpsActionFailureErrorPrefix.length)
        : undefined;
}

// Why a Click or Write did not happen, in the operator's words.
export function describeRbsIpsActionFailure(input: {
    readonly elementDisplayName: string;
    readonly action: 'click' | 'write';
    readonly status: string;
}): string {
    const reason =
        input.status === 'selector_not_found'
            ? 'was not found on the page'
            : input.status === 'selector_ambiguous'
              ? 'matches more than one element on the page'
              : input.status === 'select_option_not_found'
                ? 'has no option matching the value'
                : input.status === 'select_option_ambiguous'
                  ? 'has several options matching the value'
                  : 'could not be used';
    const consequence =
        input.action === 'click'
            ? 'so it could not be clicked'
            : 'so nothing could be written into it';
    return `Transaction details could not be read: ${input.elementDisplayName} ${reason}, ${consequence}.`;
}
